import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Collapse,
  FormControl,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useMonthlyDepletionOrders } from '../api/depletions';
import type { MonthlyDepletionOrder } from '../api/depletions';
import { shortProductLabel } from '../lib/products';
import { fmt6PkNumber } from '../lib/volume';
import { colors } from '../theme';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num,
  premisesLabel,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { PremisesBadge } from './PremisesBadge';
import { SectionExplainer } from './SectionExplainer';
import { useSectionExport } from '../hooks/useSectionExport';
import { PremiumPagination } from './depletions/PremiumPagination';

const fmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const fmtInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// The depletions feed is monthly, so "orders" are scoped to calendar 2026.
const YEAR = 2026;
const RANGE = { from: `${YEAR}-01-01`, to: `${YEAR}-12-31` };
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function monthShort(iso: string): string {
  const [y, m] = iso.split('-');
  const mi = Number(m) - 1;
  if (!y || mi < 0 || mi > 11) return iso;
  return `${MONTH_NAMES[mi]} ${y}`;
}

type SortKey = 'volume' | 'account' | 'months';
const SORT_LABEL: Record<SortKey, string> = {
  volume: 'Volume (high→low)',
  account: 'Account (A–Z)',
  months: 'Most active months',
};
// Export Info wording: names the measure and its direction.
const SORT_EXPORT_LABEL: Record<SortKey, string> = {
  volume: 'Volume (high → low)',
  account: 'Account (A → Z)',
  months: 'Active months (high → low)',
};

// Window presets. Anything else is a specific month ISO ("2026-07-01").
type WindowKey = 'all' | 'last3' | 'latest';

const DEFAULT_ROWS = 10;

// Stable, tasteful color per product so a SKU reads the same across every
// account's bars + legend. Keyed by the short label.
const PRODUCT_COLOR: Record<string, string> = {
  Rye: '#c0603a',
  Bourbon: '#bb8c3f',
  '12yr': '#3f6bb0',
  '15yr': '#4f9d7a',
  '8yr': '#2f9e9e',
  'Jack Carr': '#a03c6e',
  'Single Barrel': '#5a6b8c',
  'Hull Reserve': '#c99a2e',
  Cabernet: '#8c3a52',
  'Petite Sirah': '#6a4a8c',
  Zinfandel: '#a8683a',
};
const FALLBACK_COLORS = ['#4f6bb0', '#4f9d7a', '#c0603a', '#a03c6e', '#6a4a8c', '#2f9e9e'];
function productColor(name: string, idx: number): string {
  return PRODUCT_COLOR[shortProductLabel(name)] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length]!;
}

// One account's orders across the windowed months.
interface AccountGroup {
  account_id: number;
  name: string;
  address: string | null;
  state_code: string | null;
  city: string | null;
  county: string | null;
  zip_code: string | null;
  premises_type: string | null;
  is_online_retailer: boolean;
  months: MonthlyDepletionOrder[]; // newest first
  total9l: number;
  latestMonth: string;
}

export function DepletionsMonthlyOrders() {
  const { data, isLoading, error } = useMonthlyDepletionOrders(RANGE);
  const [windowSel, setWindowSel] = useState<WindowKey | string>('all');
  const [sort, setSort] = useState<SortKey>('volume');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const orders = useMemo(() => data?.orders ?? [], [data?.orders]);

  // Distinct months present, newest first — drives the month picker + windows.
  const months = useMemo(() => {
    const set = new Set(orders.map((o) => o.period_month));
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [orders]);

  const windowMonths = useMemo(() => {
    if (windowSel === 'all') return new Set(months);
    if (windowSel === 'latest') return new Set(months.slice(0, 1));
    if (windowSel === 'last3') return new Set(months.slice(0, 3));
    return new Set([windowSel]); // a specific month ISO
  }, [windowSel, months]);

  // Full month axis for the window (ascending) so every expanded account
  // shows the same months — one with no order still appears.
  const axisMonths = useMemo(
    () => [...months].filter((m) => windowMonths.has(m)).sort(),
    [months, windowMonths],
  );

  // Group the windowed orders by account.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byId = new Map<number, AccountGroup>();
    for (const o of orders) {
      if (!windowMonths.has(o.period_month)) continue;
      if (q && !o.name.toLowerCase().includes(q) && !(o.city ?? '').toLowerCase().includes(q))
        continue;
      let g = byId.get(o.account_id);
      if (!g) {
        g = {
          account_id: o.account_id,
          name: o.name,
          address: o.address,
          state_code: o.state_code,
          city: o.city,
          county: o.county,
          zip_code: o.zip_code,
          premises_type: o.premises_type,
          is_online_retailer: o.is_online_retailer,
          months: [],
          total9l: 0,
          latestMonth: o.period_month,
        };
        byId.set(o.account_id, g);
      }
      g.months.push(o);
      g.total9l += Number(o.cases_9l);
      if (o.period_month > g.latestMonth) g.latestMonth = o.period_month;
    }
    const list = Array.from(byId.values());
    for (const g of list) g.months.sort((a, b) => (a.period_month < b.period_month ? 1 : -1));
    list.sort((a, b) => {
      if (sort === 'account') return a.name.localeCompare(b.name);
      if (sort === 'months') return b.months.length - a.months.length || b.total9l - a.total9l;
      return b.total9l - a.total9l; // volume
    });
    return list;
  }, [orders, windowMonths, search, sort]);

  useEffect(() => {
    setPage(0);
  }, [windowSel, sort, search, rowsPerPage]);

  const totalVol9l = useMemo(() => groups.reduce((acc, g) => acc + g.total9l, 0), [groups]);
  const pageGroups =
    rowsPerPage === -1
      ? groups
      : groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Export: every account in the current window / search / sort, plus the
  // per-month detail behind each — not just the visible page.
  useSectionExport(() => {
    const first = axisMonths[0];
    const last = axisMonths[axisMonths.length - 1];
    const windowLabel =
      windowSel === 'all'
        ? `All ${YEAR} (year to date)`
        : windowSel === 'last3'
          ? 'Last 3 months (~90 days)'
          : windowSel === 'latest'
            ? 'Latest month'
            : monthShort(windowSel);
    return buildOrdersExport(groups, [
      { label: 'Time window', value: windowLabel },
      { label: 'Search (account or city)', value: search.trim() ? `"${search.trim()}"` : '' },
      { kind: 'sort', label: 'Sorted by', value: SORT_EXPORT_LABEL[sort] },
      { kind: 'info', label: 'Data through', value: months[0] ? monthShort(months[0]) : '' },
      {
        kind: 'info',
        label: 'Months covered',
        value: first && last ? `${monthShort(first)} to ${monthShort(last)}` : '',
      },
    ]);
  }, !!data);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography sx={{ color: colors.error, fontSize: 13 }}>Failed to load orders.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
            {YEAR} Orders
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: colors.textMuted, mt: 0.25 }}>
            Depletions by account. Depletions are reported by month, so open an account to see its
            order each month, by product — filter by month or a recent window.
          </Typography>
        </Box>
        {data ? (
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              In view
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {fmtInt.format(groups.length)} accounts · {fmt6PkNumber(String(totalVol9l))} 6-pk
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* Filter bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${colors.borderLight}`,
          bgcolor: colors.surfaceSubtle,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mr: 0.25,
          }}
        >
          Show
        </Typography>
        <FormControl size="small">
          <Select
            value={windowSel}
            onChange={(e) => setWindowSel(e.target.value)}
            aria-label="Time window"
            sx={{
              minWidth: 200,
              fontSize: 13,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.6 },
              '& fieldset': { borderColor: colors.border },
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 380 } } }}
          >
            <MenuItem value="all" sx={{ fontSize: 13 }}>
              All {YEAR} (year to date)
            </MenuItem>
            <MenuItem value="last3" sx={{ fontSize: 13 }}>
              Last 3 months (~90 days)
            </MenuItem>
            <MenuItem value="latest" sx={{ fontSize: 13 }}>
              Latest month{months[0] ? ` (${monthShort(months[0])})` : ''}
            </MenuItem>
            <MenuItem disabled sx={{ fontSize: 11, opacity: 0.6 }}>
              — or pick a month —
            </MenuItem>
            {months.map((m) => (
              <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
                {monthShort(m)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Sort accounts"
            sx={{
              fontSize: 12.5,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.5 },
              '& fieldset': { borderColor: colors.border },
            }}
          >
            {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 12.5 }}>
                Sort: {SORT_LABEL[k]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ position: 'relative', flex: '1 1 180px', minWidth: 150 }}>
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 17,
              color: colors.textMuted,
              pointerEvents: 'none',
            }}
          />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account or city…"
            sx={{
              width: '100%',
              fontSize: 13,
              pl: 4,
              pr: 1,
              py: 0.4,
              bgcolor: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: 1.5,
            }}
          />
        </Box>
      </Box>

      {/* Account list — calm rows; expand for the month-by-month detail. */}
      <Box
        sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: 1.5, overflow: 'hidden' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.75,
            bgcolor: colors.surfaceSubtle,
            borderBottom: `1px solid ${colors.borderLight}`,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          <Box sx={{ width: 20, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 130 }}>Account</Box>
          <Box
            sx={{
              width: 70,
              flexShrink: 0,
              textAlign: 'right',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Orders
          </Box>
          <Box sx={{ width: 120, flexShrink: 0, textAlign: 'right' }}>Total volume</Box>
        </Box>

        {isLoading || !data ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Box>
        ) : groups.length === 0 ? (
          <Typography sx={{ p: 2, fontSize: 12.5, color: colors.textMuted }}>
            {search ? `No accounts match “${search}” in this window.` : 'No orders in this window.'}
          </Typography>
        ) : (
          pageGroups.map((g, i) => {
            const isOpen = expanded.has(g.account_id);
            return (
              <Box
                key={g.account_id}
                sx={{ borderTop: i === 0 ? 'none' : `1px solid ${colors.borderLight}` }}
              >
                {/* Row */}
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(g.account_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(g.account_id);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                    bgcolor: isOpen ? colors.surfaceSubtle : 'transparent',
                    '&:hover': { bgcolor: colors.surfaceSubtle },
                  }}
                >
                  <KeyboardArrowRightIcon
                    sx={{
                      width: 20,
                      flexShrink: 0,
                      fontSize: 18,
                      color: colors.textMuted,
                      transition: 'transform 0.15s',
                      transform: isOpen ? 'rotate(90deg)' : 'none',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 130 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: colors.textPrimary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {g.name}
                      </Typography>
                      <PremisesBadge premises={g.premises_type} online={g.is_online_retailer} />
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
                      {[g.city, g.state_code].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 70,
                      flexShrink: 0,
                      textAlign: 'right',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: colors.textSecondary,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {g.months.length}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: colors.textMuted }}>
                      {g.months.length === 1 ? 'month' : 'months'}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 120, flexShrink: 0, textAlign: 'right' }}>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: colors.textPrimary,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {fmt6PkNumber(String(g.total9l))} 6-pk
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: colors.textMuted,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {fmt.format(g.total9l)} 9L
                    </Typography>
                  </Box>
                </Box>

                {/* Detail — one clean horizontal bar per month, colored by
                    product, so it's obvious what shipped when. */}
                <Collapse in={isOpen} unmountOnExit>
                  <AccountOrderDetail g={g} axisMonths={axisMonths} />
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>

      {/* Pagination — shared premium control (numbered pills, rows-per-page). */}
      {data && groups.length > 0 ? (
        <PremiumPagination
          count={groups.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          itemLabel="accounts"
        />
      ) : null}

      <SectionExplainer
        rows={[
          {
            sample: (
              <Box
                sx={{ display: 'flex', width: 26, height: 10, borderRadius: 5, overflow: 'hidden' }}
              >
                <Box sx={{ width: '55%', bgcolor: PRODUCT_COLOR['12yr'] }} />
                <Box sx={{ width: '25%', bgcolor: PRODUCT_COLOR.Bourbon }} />
                <Box sx={{ width: '20%', bgcolor: colors.borderLight }} />
              </Box>
            ),
            term: 'Monthly order bars',
            desc: (
              <>
                Open an account to see one <strong>bar per month</strong>, filled and{' '}
                <strong>colored by product</strong> and scaled to that account&apos;s biggest month.
                Hover a bar for the exact breakdown; a month with no order is labelled{' '}
                <strong>“no orders”</strong>.
              </>
            ),
          },
          {
            sample: <PremisesBadge premises="OFF" />,
            term: 'Premises badge',
            desc: (
              <>
                The account&apos;s channel — <strong>ON</strong>, <strong>OFF</strong>,{' '}
                <strong>Online</strong> (violet e-commerce), or <strong>NA</strong>.
              </>
            ),
          },
          {
            sample: (
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary }}>
                ~90d
              </Typography>
            ),
            term: 'Windows',
            desc: (
              <>
                Depletions are monthly, so <strong>“Last 3 months (~90 days)”</strong> is the
                closest to a rolling 90-day window. Pick a single month for a snapshot.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

const physicalTotal = (g: AccountGroup): number =>
  g.months.reduce((acc, m) => acc + (num(m.cases_physical) ?? 0), 0);

function buildOrdersExport(groups: AccountGroup[], filters: ExportFilter[]): ExcelWorkbookSpec {
  return {
    title: `${YEAR} Orders`,
    filters,
    sheets: [
      excelSheet<AccountGroup>({
        name: 'Accounts',
        rows: groups,
        columns: [
          ...accountColumns<AccountGroup>(),
          {
            header: 'Premises',
            value: (g) => premisesLabel(g.premises_type, g.is_online_retailer),
            width: 12,
          },
          { header: 'Total 9L', value: (g) => g.total9l, format: 'decimal' },
          { header: 'Total Physical', value: physicalTotal, format: 'decimal' },
          { header: 'Months Active', value: (g) => g.months.length, format: 'int' },
          { header: 'Latest Month', value: (g) => isoDate(g.latestMonth), format: 'date' },
        ],
      }),
      excelSheet<MonthlyDepletionOrder>({
        name: 'Orders by Month',
        rows: groups.flatMap((g) => g.months),
        columns: [
          ...accountColumns<MonthlyDepletionOrder>(),
          {
            header: 'Premises',
            value: (m) => premisesLabel(m.premises_type, m.is_online_retailer),
            width: 12,
          },
          { header: 'Month', value: (m) => isoDate(m.period_month), format: 'date' },
          { header: '9L', value: (m) => num(m.cases_9l), format: 'decimal' },
          { header: 'Physical', value: (m) => num(m.cases_physical), format: 'decimal' },
          { header: 'Product Count', value: (m) => m.product_count, format: 'int' },
          {
            header: 'Products (9L)',
            value: (m) =>
              [...m.products]
                .sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l))
                .map((p) => `${p.name} (${(num(p.cases_9l) ?? 0).toFixed(1)})`)
                .join('; '),
            width: 60,
          },
        ],
      }),
    ],
  };
}

// The expanded detail for one account: a clean product legend + one
// horizontal, product-segmented bar per month across the window.
function AccountOrderDetail({ g, axisMonths }: { g: AccountGroup; axisMonths: string[] }) {
  const byMonth = new Map(g.months.map((m) => [m.period_month, m] as const));
  const accountMax = Math.max(...g.months.map((m) => Number(m.cases_9l)), 1);

  // Product order (by total volume) + a stable color each.
  const pTotals = new Map<string, number>();
  for (const m of g.months)
    for (const p of m.products)
      pTotals.set(p.name, (pTotals.get(p.name) ?? 0) + Number(p.cases_9l));
  const productNames = [...pTotals.entries()].sort((a, b) => b[1] - a[1]).map((e) => e[0]);
  const colorByName = new Map<string, string>(
    productNames.map((n, idx) => [n, productColor(n, idx)]),
  );

  return (
    <Box
      sx={{
        bgcolor: colors.surfaceSubtle,
        borderTop: `1px solid ${colors.borderLight}`,
        px: { xs: 2, sm: 3 },
        py: 2.25,
      }}
    >
      {/* Product legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.75 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Products
        </Typography>
        {productNames.map((n) => (
          <Box key={n} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: colorByName.get(n) }} />
            <Typography sx={{ fontSize: 11.5, color: colors.textSecondary }}>
              {shortProductLabel(n)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* One horizontal bar per month */}
      <Box sx={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 0.85 }}>
        {axisMonths.map((iso) => {
          const m = byMonth.get(iso);
          const total = m ? m.products.reduce((a, p) => a + Number(p.cases_9l), 0) : 0;
          return (
            <Box key={iso} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                sx={{
                  width: 48,
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: total > 0 ? colors.textSecondary : colors.textMuted,
                }}
              >
                {monthShort(iso).split(' ')[0]}
              </Typography>
              <Tooltip
                arrow
                placement="top"
                title={<MonthCellTip iso={iso} m={m} colorByName={colorByName} />}
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#fff',
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 1.5,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                      p: 1,
                      maxWidth: 240,
                    },
                  },
                  arrow: {
                    sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
                  },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 14,
                    borderRadius: 7,
                    bgcolor: '#fff',
                    border: `1px solid ${colors.borderLight}`,
                    overflow: 'hidden',
                    display: 'flex',
                    cursor: 'help',
                  }}
                >
                  {m
                    ? productNames.map((pn) => {
                        const line = m.products.find((x) => x.name === pn);
                        const v = line ? Number(line.cases_9l) : 0;
                        if (v <= 0) return null;
                        return (
                          <Box
                            key={pn}
                            sx={{
                              width: `${(v / accountMax) * 100}%`,
                              bgcolor: colorByName.get(pn),
                            }}
                          />
                        );
                      })
                    : null}
                </Box>
              </Tooltip>
              <Box sx={{ width: 96, flexShrink: 0, textAlign: 'right' }}>
                {total > 0 ? (
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {fmt6PkNumber(String(total))} 6-pk
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontStyle: 'italic' }}>
                    no orders
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Hover breakdown for a month's bar.
function MonthCellTip({
  iso,
  m,
  colorByName,
}: {
  iso: string;
  m: MonthlyDepletionOrder | undefined;
  colorByName: Map<string, string>;
}) {
  const rows = m ? [...m.products].sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l)) : [];
  const total = rows.reduce((a, p) => a + Number(p.cases_9l), 0);
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography
        sx={{ fontSize: 11.5, fontWeight: 700, color: colors.goldDark, mb: rows.length ? 0.5 : 0 }}
      >
        {monthShort(iso)}
      </Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
          No orders
        </Typography>
      ) : null}
      {rows.map((p) => (
        <Box key={p.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <Box
            sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: colorByName.get(p.name) }}
          />
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, flex: 1 }}>
            {shortProductLabel(p.name)}
          </Typography>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: colors.textPrimary,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {fmt6PkNumber(String(p.cases_9l))}
          </Typography>
        </Box>
      ))}
      {rows.length ? (
        <Box
          sx={{ mt: 0.5, pt: 0.5, borderTop: `1px solid ${colors.borderLight}`, display: 'flex' }}
        >
          <Typography sx={{ fontSize: 11, color: colors.textMuted, flex: 1 }}>Total</Typography>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 800,
              color: colors.textPrimary,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {fmt6PkNumber(String(total))} 6-pk
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
